import type {
  Chat,
  Message,
  MessageDeletedPayload,
  MessageHistoryPage,
  MessageUpdatedPayload,
  PresencePayload,
  ServerFrame,
  TypingPayload,
} from '@liniya/api-client';
import type { QueryClient } from '@tanstack/react-query';

import { queryKeys } from '../query-keys';
import { isVisible } from '../platform';
import { usePendingStore } from '../store/pending-messages';
import { usePresenceStore } from '../store/presence';

type InfiniteHistory = {
  pages: MessageHistoryPage[];
  pageParams: unknown[];
};

/**
 * Insert or replace one message in the cached history of its chat.
 *
 * Messages are keyed by their id, which the sending client generated, so a
 * message delivered twice — once as the reply to the send, once over the
 * socket — collapses into one entry rather than appearing twice. Ordering is
 * by server sequence, never by arrival time.
 */
function upsertMessage(client: QueryClient, message: Message): void {
  client.setQueryData<InfiniteHistory>(queryKeys.messages(message.chat_id), (current) => {
    if (!current || current.pages.length === 0) return current;
    const pages = current.pages.map((page) => ({
      ...page,
      messages: page.messages.filter((item) => item.message_id !== message.message_id),
    }));
    const lastIndex = pages.length - 1;
    const last = pages[lastIndex];
    if (!last) return current;
    const merged = [...last.messages, message].sort(
      (left, right) => left.server_sequence - right.server_sequence,
    );
    pages[lastIndex] = { ...last, messages: merged };
    return { ...current, pages };
  });
}

function patchMessage(
  client: QueryClient,
  chatId: string,
  messageId: string,
  patch: Partial<Message>,
): void {
  client.setQueryData<InfiniteHistory>(queryKeys.messages(chatId), (current) => {
    if (!current) return current;
    return {
      ...current,
      pages: current.pages.map((page) => ({
        ...page,
        messages: page.messages.map((item) =>
          item.message_id === messageId ? { ...item, ...patch } : item,
        ),
      })),
    };
  });
}

function removeMessage(client: QueryClient, chatId: string, messageId: string): void {
  client.setQueryData<InfiniteHistory>(queryKeys.messages(chatId), (current) => {
    if (!current) return current;
    return {
      ...current,
      pages: current.pages.map((page) => ({
        ...page,
        messages: page.messages.filter((item) => item.message_id !== messageId),
      })),
    };
  });
}

/**
 * Update the row this message belongs to.
 *
 * Returns false when the list holds no such row. That is not an edge case: the
 * very first message of a conversation someone else just started arrives for a
 * chat this client has never seen, and patching in place would silently drop
 * it — the conversation would stay invisible until a reload. The caller
 * refetches the list instead, which is also the only way to learn the peer's
 * name, avatar and membership.
 */
function bumpChatPreview(client: QueryClient, message: Message, viewerUserId: string): boolean {
  const chats = client.getQueryData<Chat[]>(queryKeys.chats);
  if (!chats) return true; // Nothing cached yet: the pending query will bring it.
  if (!chats.some((chat) => chat.id === message.chat_id)) return false;

  client.setQueryData<Chat[]>(queryKeys.chats, (current) =>
    (current ?? []).map((chat) => {
      if (chat.id !== message.chat_id) return chat;
      const fromSomeoneElse = message.sender_user_id !== viewerUserId;
      return {
        ...chat,
        updated_at: message.created_at,
        last_message_preview: previewFor(message),
        unread_count: fromSomeoneElse ? chat.unread_count + 1 : chat.unread_count,
      };
    }),
  );
  return true;
}

function previewFor(message: Message): string {
  if (message.deleted) return 'Сообщение удалено';
  const content = message.content ?? '';
  if (!content) return 'Зашифрованное сообщение';
  return content.replace(/\s+/g, ' ').trim().slice(0, 80) || 'Сообщение';
}

export interface RealtimeHandlerContext {
  queryClient: QueryClient;
  viewerUserId: string;
  /** The chat currently on screen, so its unread count is not inflated. */
  activeChatId: () => string | null;
  markRead: (chatId: string, messageId: string) => void;
}

/**
 * Turn one server frame into cache and store updates.
 *
 * Everything the socket delivers is applied here rather than in components:
 * one place decides what an event means, so a message arriving while the user
 * is on the settings screen updates the same state as one arriving while they
 * are reading the chat.
 */
export function applyRealtimeFrame(frame: ServerFrame, context: RealtimeHandlerContext): void {
  const { queryClient, viewerUserId } = context;

  switch (frame.type) {
    case 'message.created': {
      const message = frame.payload as unknown as Message;
      upsertMessage(queryClient, message);
      if (!bumpChatPreview(queryClient, message, viewerUserId)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      }
      // The optimistic copy has served its purpose now that the real one exists.
      usePendingStore.getState().remove(message.chat_id, message.message_id);
      if (
        message.sender_user_id !== viewerUserId &&
        context.activeChatId() === message.chat_id &&
        isVisible()
      ) {
        context.markRead(message.chat_id, message.message_id);
      }
      return;
    }

    case 'message.updated': {
      const payload = frame.payload as unknown as MessageUpdatedPayload;
      patchMessage(queryClient, payload.chat_id, payload.message_id, {
        content: payload.content,
        edited_at: payload.edited_at,
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      return;
    }

    case 'message.deleted': {
      const payload = frame.payload as unknown as MessageDeletedPayload;
      removeMessage(queryClient, payload.chat_id, payload.message_id);
      usePendingStore.getState().remove(payload.chat_id, payload.message_id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      return;
    }

    case 'message.receipt': {
      const payload = frame.payload as unknown as {
        chat_id: string;
        message_id: string;
        state: 'delivered' | 'read';
      };
      patchMessage(queryClient, payload.chat_id, payload.message_id, {
        receiptState: payload.state,
      });
      return;
    }

    case 'typing.updated': {
      const payload = frame.payload as unknown as TypingPayload;
      if (payload.user_id === viewerUserId) return;
      usePresenceStore
        .getState()
        .setTyping(payload.chat_id, payload.user_id, payload.typing, payload.ttl_seconds);
      return;
    }

    case 'presence.updated': {
      const payload = frame.payload as unknown as PresencePayload;
      usePresenceStore.getState().setOnline(payload.user_id, payload.online);
      void queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      return;
    }

    case 'chat.updated':
    case 'group.updated':
    case 'channel.updated': {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      return;
    }

    case 'device.updated': {
      void queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      return;
    }

    default:
      // Unknown event types are ignored on purpose: a newer server must be
      // able to add events without an older client treating them as failures.
      return;
  }
}

export const realtimeCache = { upsertMessage, patchMessage, removeMessage, previewFor };
